import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../utils/axiosConfig';
import '../takip/takip.css';

// v1.24.0 — Müşteri / potansiyel müşterinin "İlgilendiği İlanlar" kriterleri.
// Kriterlere uyan yeni daire / emlak / kiralık kaydı girilince Dashboard'da "Potansiyel Satış Fırsatı" oluşur.

export interface IlgiTalebi {
    talepID: number;
    cariID?: number | null;
    leadID?: number | null;
    ilanTuru: 'SATILIK' | 'KIRALIK' | 'HEPSI';
    kategori: 'KONUT' | 'ARSA' | 'ISYERI' | 'HEPSI';
    fiyatMin?: number | null;
    fiyatMax?: number | null;
    butce?: number | null;
    m2Min?: number | null;
    m2Max?: number | null;
    daireTipIDler?: string | null;
    binaYasiMax?: number | null;
    odemeSekli?: 'PESIN' | 'KREDI' | 'TAKSIT' | 'TAKAS' | null;
    projeID?: number | null;
    konum?: string | null;
    notlar?: string | null;
    metinYorumu?: string | null;   // sunucu üretir (v1.25.0) — Konum + Notlar'dan çıkarılan ölçütler (JSON)
    aktifMi: boolean;
    olusturmaTarihi?: string;
}

interface TalepTerimi { ad: string; varyantlar: string[]; }
interface TalepYorumu {
    kaynak: 'yerel' | 'ai';
    fiyatMin?: number | null; fiyatMax?: number | null; butce?: number | null;
    m2Min?: number | null; m2Max?: number | null;
    odaTipleri: string[]; kategori?: string | null; ilanTuru?: string | null;
    konumlar: TalepTerimi[]; ozellikler: TalepTerimi[];
}
const yorumOku = (s?: string | null): TalepYorumu | null => { if (!s) return null; try { return JSON.parse(s); } catch { return null; } };

// Türkçe tutar okuma: "3 milyon", "3,5m", "üç buçuk milyon", "750 bin", "1 milyon 250 bin", "3.000.000 TL" (sunucudaki TrMetin.Tutar ile aynı kurallar)
const KELIMELER: Record<string, number> = {
    bir: 1, iki: 2, uc: 3, dort: 4, bes: 5, alti: 6, yedi: 7, sekiz: 8, dokuz: 9, on: 10, yirmi: 20, otuz: 30, kirk: 40,
    elli: 50, altmis: 60, yetmis: 70, seksen: 80, doksan: 90, yuz: 100,
};
const TR_HARF: Record<string, string> = { 'ç': 'c', 'ğ': 'g', 'ı': 'i', 'ö': 'o', 'ş': 's', 'ü': 'u', 'â': 'a', 'î': 'i', 'û': 'u' };
const katla = (s: string) => s.toLocaleLowerCase('tr-TR').replace(/[çğıöşüâîû]/g, c => TR_HARF[c]).replace(/\s+/g, ' ').trim();
const rakam = (s: string): number | null => {
    const t = /^\d{1,3}([.,]\d{3})+$/.test(s) ? s.replace(/[.,]/g, '') : s.replace(',', '.');
    const v = Number(t);
    return isNaN(v) ? null : v;
};
export const tutarCoz = (s: string): number | null => {
    let k = katla(s)
        .replace(/\b((?:(?:bir|iki|uc|dort|bes|alti|yedi|sekiz|dokuz|on|yirmi|otuz|kirk|elli|altmis|yetmis|seksen|doksan|yuz)\s+)+)(bucuk\s+)?(?=(milyar|milyon|bin)\b)/g,
            (_m, sozcukler: string, bucuk?: string) => {
                let p = 0;
                for (const w of sozcukler.trim().split(' ')) { const v = KELIMELER[w]; p = v === 100 ? (p || 1) * 100 : p + v; }
                return `${p + (bucuk ? 0.5 : 0)} `;
            })
        .replace(/(\d)\s*bucuk/g, '$1.5')
        .replace(/(\d)\s*(milyar|milyon)[a-z']*/g, '$1 $2 ')
        .replace(/(\d)\s*bin(?:'?(?:e|den|lik|luk|in|i|dir))?(?![a-z])/g, '$1 bin ')
        .replace(/\b(tl|try|lira)\b|₺/g, ' ').trim();
    if (!k) return null;
    const carpan = (c?: string) => c === 'milyar' ? 1e9 : c === 'milyon' || c === 'mn' || c === 'm' ? 1e6 : c === 'bin' || c === 'k' ? 1e3 : 1;
    let toplam = 0, bulundu = false;
    const re = /(\d+(?:[.,]\d+)*)\s*(milyar|milyon|mn|bin|k|m)?(?![a-z])/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(k))) {
        const v = rakam(m[1]);
        if (v == null) return null;
        toplam += v * carpan(m[2]);
        bulundu = true;
    }
    // Rakam dışında anlaşılmayan sözcük kaldıysa (ör. "çok") tutar sayılmaz
    if (k.replace(re, ' ').replace(/\b(civari|civarinda|kadar|yaklasik|tl)\b/g, ' ').trim().length > 0) return null;
    return bulundu && toplam > 0 ? Math.round(toplam) : null;
};

const KATEGORI_ADI: Record<string, string> = { KONUT: 'Konut', ARSA: 'Arsa / tarla', ISYERI: 'İş yeri' };

// Sunucudaki eşleştirme kuralının aynısı: metinden çıkan ölçüt yalnızca elle doldurulmamış alanda geçerli
function yorumOzeti(t: IlgiTalebi, y: TalepYorumu): { metin: string; ipucu?: string }[] {
    const p: { metin: string; ipucu?: string }[] = [];
    if (t.ilanTuru === 'HEPSI' && y.ilanTuru) p.push({ metin: y.ilanTuru === 'KIRALIK' ? 'Kiralık' : 'Satılık' });
    if (t.kategori === 'HEPSI' && y.kategori) p.push({ metin: KATEGORI_ADI[y.kategori] ?? y.kategori });
    if (t.fiyatMin == null && t.fiyatMax == null && t.butce == null) {
        if (y.fiyatMin != null || y.fiyatMax != null) p.push({ metin: `${y.fiyatMin != null ? para(y.fiyatMin) : '…'} – ${y.fiyatMax != null ? para(y.fiyatMax) : '…'} ₺` });
        if (y.butce != null) p.push({ metin: `bütçe ${para(y.butce)} ₺` });
    }
    if (t.m2Min == null && t.m2Max == null && (y.m2Min != null || y.m2Max != null)) p.push({ metin: `${y.m2Min ?? '…'}–${y.m2Max ?? '…'} m²` });
    if (!t.daireTipIDler && y.odaTipleri?.length) p.push({ metin: y.odaTipleri.join('/') });
    // Yerel yorumda konum, Konum alanının kendisidir (zaten özette) — yalnızca AI'ın eklediği yazımlar gösterilir
    if (y.kaynak === 'ai' || !t.konum) (y.konumlar ?? []).forEach(k => p.push({ metin: `📍 ${k.ad}`, ipucu: `Aranan yazımlar: ${k.varyantlar.join(', ')}` }));
    (y.ozellikler ?? []).forEach(o => p.push({ metin: `+ ${o.ad}`, ipucu: `İlanda aranan: ${o.varyantlar.join(', ')} (uyan ilana puan ekler, eleme yapmaz)` }));
    return p;
}

const ILAN_TURLERI = [['SATILIK', 'Satılık'], ['KIRALIK', 'Kiralık'], ['HEPSI', 'Fark etmez']] as const;
const KATEGORILER = [['KONUT', 'Konut'], ['ARSA', 'Arsa / tarla'], ['ISYERI', 'İş yeri'], ['HEPSI', 'Fark etmez']] as const;
const ODEMELER = [['', '—'], ['PESIN', 'Peşin'], ['KREDI', 'Kredi'], ['TAKSIT', 'Taksit'], ['TAKAS', 'Takas']] as const;

const para = (n?: number | null) => n == null ? '' : n.toLocaleString('tr-TR', { maximumFractionDigits: 0 });

let tipOnbellek: { daireTipID: number; tip: string }[] | null = null;
let projeOnbellek: { projeID: number; projeAdi: string }[] | null = null;

export function talepOzeti(t: IlgiTalebi, tipler: { daireTipID: number; tip: string }[], projeler: { projeID: number; projeAdi: string }[]): string {
    const p: string[] = [];
    p.push(ILAN_TURLERI.find(x => x[0] === t.ilanTuru)?.[1] ?? '');
    if (t.kategori !== 'HEPSI') p.push(KATEGORILER.find(x => x[0] === t.kategori)?.[1] ?? '');
    const tipAdlari = (t.daireTipIDler ?? '').split(',').filter(Boolean).map(id => tipler.find(x => x.daireTipID === Number(id))?.tip).filter(Boolean);
    if (tipAdlari.length) p.push(tipAdlari.join('/'));
    if (t.fiyatMin != null || t.fiyatMax != null) p.push(`${t.fiyatMin != null ? para(t.fiyatMin) : '…'} – ${t.fiyatMax != null ? para(t.fiyatMax) : '…'} ₺`);
    if (t.butce != null) p.push(`bütçe ${para(t.butce)} ₺`);
    if (t.m2Min != null || t.m2Max != null) p.push(`${t.m2Min ?? '…'}–${t.m2Max ?? '…'} m²`);
    if (t.binaYasiMax != null) p.push(`en fazla ${t.binaYasiMax} yaşında`);
    if (t.odemeSekli) p.push(ODEMELER.find(x => x[0] === t.odemeSekli)?.[1] ?? '');
    if (t.projeID) p.push(projeler.find(x => x.projeID === t.projeID)?.projeAdi ?? 'proje');
    if (t.konum) p.push(t.konum);
    return p.filter(Boolean).join(' · ');
}

interface Props { cariID?: number | null; leadID?: number | null; }

const bos = (cariID?: number | null, leadID?: number | null): IlgiTalebi =>
    ({ talepID: 0, cariID, leadID, ilanTuru: 'SATILIK', kategori: 'KONUT', aktifMi: true });

const IlgiTalepleri: React.FC<Props> = ({ cariID, leadID }) => {
    const navigate = useNavigate();
    const [liste, setListe] = useState<{ talep: IlgiTalebi; firsatSayisi: number }[] | null>(null);
    const [form, setForm] = useState<IlgiTalebi | null>(null);
    const [tipler, setTipler] = useState(tipOnbellek ?? []);
    const [projeler, setProjeler] = useState(projeOnbellek ?? []);
    const [hata, setHata] = useState('');
    const [kaydediliyor, setKaydediliyor] = useState(false);
    const [uyanlar, setUyanlar] = useState<{ talepID: number; liste: any[] | null } | null>(null);
    // Tutar alanlarında yazılan ham metin ("3 milyon") — alan odaktan çıkınca biçimli sayı gösterilir
    const [ham, setHam] = useState<Partial<Record<'fiyatMin' | 'fiyatMax' | 'butce', string>>>({});
    const [yorumlanan, setYorumlanan] = useState<number | null>(null);

    const yukle = useCallback(async () => {
        if (!cariID && !leadID) return;
        try {
            const r = await axiosInstance.get('/api/IlgiTalepleri', { params: cariID ? { cariID } : { leadID } });
            setListe(r.data);
        } catch { setListe([]); }
    }, [cariID, leadID]);
    useEffect(() => { yukle(); }, [yukle]);
    useEffect(() => {
        if (!tipOnbellek) axiosInstance.get('/api/DaireTipleri').then(r => { tipOnbellek = (r.data as any[]).map(x => ({ daireTipID: x.daireTipID, tip: x.tip })); setTipler(tipOnbellek); }).catch(() => { });
        if (!projeOnbellek) axiosInstance.get('/api/Projeler').then(r => { projeOnbellek = (r.data as any[]).filter(p => p.aktifMi !== false).map(p => ({ projeID: p.projeID, projeAdi: p.projeAdi })); setProjeler(projeOnbellek); }).catch(() => { });
    }, []);

    const kaydet = async () => {
        if (!form) return;
        const anlasilmayan = (Object.entries(ham) as [string, string][]).find(([, v]) => v.trim() && tutarCoz(v) == null);
        if (anlasilmayan) { setHata(`Tutar anlaşılamadı: "${anlasilmayan[1]}". Ör. 3 milyon, 3,5m, 750 bin, 3.000.000`); return; }
        setKaydediliyor(true); setHata('');
        try {
            if (form.talepID) await axiosInstance.post(`/api/IlgiTalepleri/update/${form.talepID}`, form);
            else await axiosInstance.post('/api/IlgiTalepleri', form);
            setForm(null); setHam({});
            yukle();
        } catch (e: any) { setHata(e?.response?.data?.message || 'Kaydedilemedi.'); }
        finally { setKaydediliyor(false); }
    };
    const sil = async (id: number) => {
        if (!window.confirm('İlgi kaydı silinsin mi? (Bu kayda bağlı fırsatlar da silinir.)')) return;
        try { await axiosInstance.post(`/api/IlgiTalepleri/delete/${id}`); yukle(); } catch { }
    };
    const yenidenYorumla = async (id: number) => {
        setYorumlanan(id);
        try { await axiosInstance.post(`/api/IlgiTalepleri/${id}/yorumla`); await yukle(); if (uyanlar?.talepID === id) setUyanlar(null); }
        catch { } finally { setYorumlanan(null); }
    };
    const formAc = (f: IlgiTalebi) => { setForm(f); setHam({}); setHata(''); };
    const uyanlariGoster = async (id: number) => {
        if (uyanlar?.talepID === id) { setUyanlar(null); return; }
        setUyanlar({ talepID: id, liste: null });
        try { const r = await axiosInstance.get(`/api/IlgiTalepleri/${id}/uyanlar`); setUyanlar({ talepID: id, liste: r.data }); }
        catch { setUyanlar({ talepID: id, liste: [] }); }
    };

    const alan = (k: keyof IlgiTalebi, v: any) => setForm(f => f ? { ...f, [k]: v } : f);
    const tutarAlani = (k: 'fiyatMin' | 'fiyatMax' | 'butce', etiket: string) => {
        const h = ham[k];
        const deger = h != null && h.trim() ? tutarCoz(h) : null;
        return (
            <div className="tkp-alan"><label className="tkp-etiket">{etiket}</label>
                <input className="tkp-girdi" value={h ?? para(form?.[k])} placeholder="Ör. 3 milyon"
                    onChange={e => { const v = e.target.value; setHam(x => ({ ...x, [k]: v })); alan(k, v.trim() ? tutarCoz(v) : null); }}
                    onBlur={() => { if (h == null || !h.trim() || deger != null) setHam(x => { const y = { ...x }; delete y[k]; return y; }); }} />
                {h != null && h.trim() !== '' && !/^[\d.]+$/.test(h.trim()) && (
                    <div className="tkp-ipucu" style={{ color: deger != null ? '#047857' : '#b91c1c', fontWeight: 600 }}>
                        {deger != null ? `= ${para(deger)} ₺` : 'Tutar anlaşılamadı'}
                    </div>
                )}
            </div>
        );
    };
    const secilenTipler = (form?.daireTipIDler ?? '').split(',').filter(Boolean).map(Number);
    const tipSec = (id: number) => {
        const yeni = secilenTipler.includes(id) ? secilenTipler.filter(x => x !== id) : [...secilenTipler, id];
        alan('daireTipIDler', yeni.length ? yeni.join(',') : null);
    };
    const secBtn = (aktif: boolean): React.CSSProperties => ({
        border: `1.5px solid ${aktif ? '#fdba74' : '#d1d5db'}`, background: aktif ? '#fff7ed' : '#fff', color: aktif ? '#c2410c' : '#475569',
        borderRadius: 7, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
    });

    return (
        <div>
            {liste === null && <div className="tkp-bos"><i className="fas fa-spinner fa-spin"></i></div>}
            {liste && liste.length === 0 && !form && <div className="tkp-bos">Henüz ilgi kaydı yok. Aradığı ilan özelliklerini girin; uygun yeni kayıt girilince satış fırsatı oluşur.</div>}
            {liste?.map(({ talep: t, firsatSayisi }) => (
                <div key={t.talepID} style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: '9px 11px', marginBottom: 8, background: t.aktifMi ? '#fff' : '#f8fafc', opacity: t.aktifMi ? 1 : 0.65 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                        <i className="fas fa-search-dollar" style={{ color: '#e67d21', marginTop: 3 }}></i>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#1a2440' }}>{talepOzeti(t, tipler, projeler)}{!t.aktifMi && ' (pasif)'}</div>
                            {t.notlar && <div style={{ fontSize: 12, color: '#64748b', marginTop: 2, whiteSpace: 'pre-wrap' }}>{t.notlar}</div>}
                            {(() => {
                                const y = yorumOku(t.metinYorumu);
                                if (!y || (!t.konum && !t.notlar)) return null;
                                const parcalar = yorumOzeti(t, y);
                                return (
                                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center', marginTop: 5 }}>
                                        <span style={{ fontSize: 11, fontWeight: 700, color: '#6d28d9' }} title={y.kaynak === 'ai' ? 'Konum ve notlar yapay zekâ ile yorumlandı' : 'Konum ve notlar yerel kurallarla yorumlandı (yapay zekâ ayarı kapalı)'}>
                                            <i className={`fas ${y.kaynak === 'ai' ? 'fa-magic' : 'fa-spell-check'}`} style={{ marginRight: 4 }}></i>
                                            {y.kaynak === 'ai' ? 'AI yorumu:' : 'Metinden:'}
                                        </span>
                                        {parcalar.length === 0 && <span style={{ fontSize: 11, color: '#94a3b8' }}>ek ölçüt bulunamadı</span>}
                                        {parcalar.map((p, i) => <span key={i} className="tkp-rozet talep" title={p.ipucu}>{p.metin}</span>)}
                                        <button className="tkp-ikon-btn" style={{ color: '#7c3aed', width: 22, height: 22, fontSize: 11 }} title="Yeniden yorumla" disabled={yorumlanan === t.talepID} onClick={() => yenidenYorumla(t.talepID)}>
                                            <i className={`fas fa-sync-alt${yorumlanan === t.talepID ? ' fa-spin' : ''}`}></i>
                                        </button>
                                    </div>
                                );
                            })()}
                            <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                                <button className="tkp-btn kucuk" onClick={() => uyanlariGoster(t.talepID)}><i className="fas fa-list"></i>Şu an uyan ilanlar</button>
                                {firsatSayisi > 0 && <span className="tkp-rozet tamam">{firsatSayisi} fırsat</span>}
                            </div>
                        </div>
                        <button className="tkp-ikon-btn" style={{ color: '#64748b' }} onClick={() => formAc({ ...t })} title="Düzenle"><i className="fas fa-pen"></i></button>
                        <button className="tkp-ikon-btn" onClick={() => sil(t.talepID)} title="Sil"><i className="fas fa-trash"></i></button>
                    </div>
                    {uyanlar?.talepID === t.talepID && (
                        <div style={{ marginTop: 8, borderTop: '1px solid #f1f5f9', paddingTop: 6 }}>
                            {uyanlar.liste === null && <div className="tkp-bos"><i className="fas fa-spinner fa-spin"></i></div>}
                            {uyanlar.liste?.length === 0 && <div className="tkp-bos">Şu an uyan aktif ilan yok.</div>}
                            {uyanlar.liste?.map((i: any) => (
                                <div key={`${i.kayitTuru}${i.kayitID}`} className="tkp-dosya" style={{ cursor: i.url ? 'pointer' : 'default' }} onClick={() => i.url && navigate(i.url)}>
                                    <span className="tkp-rozet notr">{i.kayitTuru === 'DAIRE' ? 'Proje' : i.kayitTuru === 'EMLAK' ? 'Emlak' : 'Kiralık'}</span>
                                    <span className="ad">{i.baslik}</span>
                                    <span className="kucuk">{i.fiyat != null ? `${para(i.fiyat)} ₺` : ''}{i.m2 ? ` · ${i.m2} m²` : ''}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            ))}

            {form ? (
                <div style={{ border: '1.5px solid #fdba74', borderRadius: 10, padding: 12, background: '#fffbf5', marginTop: 6 }}>
                    {hata && <div className="tkp-hata">{hata}</div>}
                    <div className="tkp-alan">
                        <label className="tkp-etiket">İlan türü</label>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {ILAN_TURLERI.map(([v, ad]) => <button key={v} type="button" style={secBtn(form.ilanTuru === v)} onClick={() => alan('ilanTuru', v)}>{ad}</button>)}
                        </div>
                    </div>
                    <div className="tkp-alan">
                        <label className="tkp-etiket">Kategori</label>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {KATEGORILER.map(([v, ad]) => <button key={v} type="button" style={secBtn(form.kategori === v)} onClick={() => alan('kategori', v)}>{ad}</button>)}
                        </div>
                    </div>
                    {form.kategori !== 'ARSA' && tipler.length > 0 && (
                        <div className="tkp-alan">
                            <label className="tkp-etiket">Daire tipi</label>
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                {tipler.map(tp => <button key={tp.daireTipID} type="button" style={secBtn(secilenTipler.includes(tp.daireTipID))} onClick={() => tipSec(tp.daireTipID)}>{tp.tip}</button>)}
                            </div>
                        </div>
                    )}
                    <div className="tkp-satir">
                        {tutarAlani('fiyatMin', 'Fiyat en az (₺)')}
                        {tutarAlani('fiyatMax', 'Fiyat en çok (₺)')}
                        {tutarAlani('butce', 'Bütçe (₺)')}
                    </div>
                    <div className="tkp-satir">
                        <div className="tkp-alan"><label className="tkp-etiket">m² en az</label>
                            <input className="tkp-girdi" type="number" min={0} value={form.m2Min ?? ''} onChange={e => alan('m2Min', e.target.value ? Number(e.target.value) : null)} /></div>
                        <div className="tkp-alan"><label className="tkp-etiket">m² en çok</label>
                            <input className="tkp-girdi" type="number" min={0} value={form.m2Max ?? ''} onChange={e => alan('m2Max', e.target.value ? Number(e.target.value) : null)} /></div>
                        <div className="tkp-alan"><label className="tkp-etiket">Bina yaşı en çok</label>
                            <input className="tkp-girdi" type="number" min={0} value={form.binaYasiMax ?? ''} onChange={e => alan('binaYasiMax', e.target.value ? Number(e.target.value) : null)} /></div>
                    </div>
                    <div className="tkp-satir">
                        <div className="tkp-alan"><label className="tkp-etiket">Ödeme şekli</label>
                            <select className="tkp-girdi" value={form.odemeSekli ?? ''} onChange={e => alan('odemeSekli', e.target.value || null)}>
                                {ODEMELER.map(([v, ad]) => <option key={v} value={v}>{ad}</option>)}
                            </select></div>
                        <div className="tkp-alan"><label className="tkp-etiket">Proje (yalnızca)</label>
                            <select className="tkp-girdi" value={form.projeID ?? ''} onChange={e => alan('projeID', e.target.value ? Number(e.target.value) : null)}>
                                <option value="">Fark etmez</option>
                                {projeler.map(p => <option key={p.projeID} value={p.projeID}>{p.projeAdi}</option>)}
                            </select></div>
                    </div>
                    <div className="tkp-alan"><label className="tkp-etiket">Konum (virgülle birden fazla)</label>
                        <input className="tkp-girdi" value={form.konum ?? ''} maxLength={200} onChange={e => alan('konum', e.target.value)} placeholder="Ör. Çankaya, Yenimahalle" /></div>
                    <div className="tkp-alan"><label className="tkp-etiket">Notlar (serbest metin)</label>
                        <textarea className="tkp-girdi" rows={2} value={form.notlar ?? ''} onChange={e => alan('notlar', e.target.value)} placeholder="Ör. 3,5 milyona kadar 3+1 daire, bahçeli, otoparklı" /></div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, marginBottom: 8 }}>
                        <input type="checkbox" checked={form.aktifMi} onChange={e => alan('aktifMi', e.target.checked)} />Aktif (eşleştirmede kullanılsın)
                    </label>
                    <div className="tkp-ipucu" style={{ marginBottom: 8 }}>
                        Eşleşmede doldurulan her ölçüt sağlanmalı; bina yaşı ve ödeme şekli bilgi amaçlıdır.
                        Konum ve notlara yazılanlar da yorumlanır (ör. "3 milyona kadar", "konut", "bahçeli"); elle doldurulan alan her zaman önce gelir.
                    </div>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        <button className="tkp-btn" onClick={() => { setForm(null); setHata(''); setHam({}); }}>Vazgeç</button>
                        <button className="tkp-btn ana" onClick={kaydet} disabled={kaydediliyor}><i className={`fas ${kaydediliyor ? 'fa-spinner fa-spin' : 'fa-check'}`}></i>Kaydet</button>
                    </div>
                </div>
            ) : (
                <button className="tkp-btn turuncu" style={{ marginTop: 4 }} onClick={() => formAc(bos(cariID, leadID))}><i className="fas fa-plus"></i>İlgi ekle</button>
            )}
        </div>
    );
};

export default IlgiTalepleri;

// Kayıt sayfaları için düğme + modal
export const IlgiTalepButonu: React.FC<{ cariID?: number | null; leadID?: number | null; ad: string; style?: React.CSSProperties }> = ({ cariID, leadID, ad, style }) => {
    const [acik, setAcik] = useState(false);
    if (!cariID && !leadID) return null;
    return (
        <>
            <button type="button" className="tkp-kayit-btn" style={{ borderColor: '#7c3aed', color: '#6d28d9', background: '#f5f3ff', ...style }} onClick={() => setAcik(true)} title="İlgilendiği ilanlar">
                <i className="fas fa-search-dollar"></i>İlgilendiği
            </button>
            {acik && (
                <div className="tkp-perde" onMouseDown={e => { if (e.target === e.currentTarget) setAcik(false); }}>
                    <div className="tkp-modal dar">
                        <div className="tkp-modal-bas">
                            <i className="fas fa-search-dollar" style={{ color: '#c4b5fd' }}></i>
                            <h5 title={ad}>İlgilendiği İlanlar — {ad}</h5>
                            <button className="tkp-kapat" onClick={() => setAcik(false)}>×</button>
                        </div>
                        <div className="tkp-modal-govde"><IlgiTalepleri cariID={cariID} leadID={leadID} /></div>
                    </div>
                </div>
            )}
        </>
    );
};
